"use client";

import { ArrowRight, Check, Phone } from "lucide-react";
import type { HeroComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

const DEFAULT_ASSURANCES = ["Neuf ou reconditionné", "Garantie jusqu’à 12 mois", "Expédition 24/48h"];

/**
 * Product-led automotive hero. The CMS-managed workshop image carries the
 * composition and preserves a clear copy area across responsive breakpoints.
 */
export function HeroRenderer({ component }: { component: HeroComponent }) {
  const {
    title,
    subtitle,
    eyebrow,
    primaryLabel,
    primaryHref,
    secondaryLabel,
    secondaryHref,
    assurances,
    backgroundImageSrc,
    mobileBackgroundImageSrc,
  } = component.content;
  const { src } = resolveImageValue(backgroundImageSrc);
  const { src: mobileSrc } = resolveImageValue(mobileBackgroundImageSrc);
  const backgroundSrc = src || "images/hero.svg";
  const mobileBackgroundSrc = mobileSrc || "images/photos/landing-apa-team-mobile.jpg";
  const assuranceItems = assurances?.map((item) => item.trim()).filter(Boolean) ?? DEFAULT_ASSURANCES;

  return (
    <section className="relative isolate mx-[calc(50%-50vw)] w-screen overflow-hidden bg-[#07100B] text-white">
      <div className="relative md:hidden">
        <RuntimeImage
          src={mobileBackgroundSrc}
          alt="Équipe APA Parts devant l’atelier"
          className="h-[260px] w-full object-cover object-[center_15%] min-[460px]:h-[300px] min-[600px]:h-[340px]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(5,12,8,.32)_0%,rgba(5,12,8,.06)_30%,rgba(5,12,8,.22)_52%,rgba(5,12,8,.78)_72%,#07100B_96%)]" aria-hidden="true" />
      </div>
      <RuntimeImage
        src={backgroundSrc}
        alt="Technicien APA Parts contrôlant un turbocompresseur"
        className="absolute inset-0 hidden h-full w-full object-cover object-[center_58%] md:block"
        loading="eager"
      />
      <div className="absolute inset-0 hidden md:block md:bg-[linear-gradient(90deg,rgba(5,12,8,.98)_0%,rgba(5,12,8,.88)_43%,rgba(5,12,8,.28)_72%,rgba(5,12,8,.08)_100%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 hidden h-24 bg-gradient-to-t from-[#07100B] to-transparent md:block" aria-hidden="true" />

      <div className="relative mx-auto -mt-24 flex max-w-7xl items-end px-6 pb-6 min-[560px]:px-8 md:mt-0 md:h-auto md:min-h-[590px] md:max-h-none md:items-center md:px-8 md:py-14 lg:min-h-[690px] lg:pb-24 lg:pt-16">
        <div className="max-w-[690px] md:max-w-[530px] lg:max-w-[690px]">
          <p className="flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[.2em] text-[#72E2AD] drop-shadow-[0_2px_8px_rgba(0,0,0,.9)]">
            <span className="h-0.5 w-7 bg-[#00B862] md:w-9" aria-hidden="true" />
            <span className="whitespace-nowrap md:hidden">Contrôlé · Prêt à monter</span>
            <span className="hidden md:inline">{eyebrow || "Pièces diesel · Échange standard"}</span>
          </p>

          <h1 className="mt-3 text-[1.95rem] font-bold leading-[1.03] tracking-[-.035em] text-white drop-shadow-[0_3px_12px_rgba(0,0,0,.9)] min-[430px]:text-[2.15rem] sm:text-[2.65rem] md:mt-5 md:text-[3.35rem] md:leading-[.96] md:tracking-[-.045em] lg:text-[4.35rem]" style={{ fontFamily: "var(--font-aleo)" }}>
            {title}
          </h1>
          {subtitle ? <p className="mt-4 max-w-xl text-sm leading-[1.65] text-white/90 drop-shadow-[0_2px_8px_rgba(0,0,0,.9)] md:mt-5 md:text-lg md:leading-relaxed">{subtitle}</p> : null}

          <div className="mt-5 flex flex-col gap-2.5 md:mt-7 md:flex-row md:gap-3">
            <RuntimeLink href={primaryHref || { slug: "pieces-detachees" }} className="group inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[#00B862] px-5 text-sm font-extrabold text-white shadow-[0_12px_28px_-18px_rgba(0,184,98,.9)] transition hover:bg-[#009F55] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#07100B] md:px-6">
              {/* One label at every width: the CTAs are full-width on mobile now,
                  so there is room for the real CMS wording, and screen readers
                  no longer hear the button name twice. */}
              {primaryLabel || "Trouver ma pièce"}
              <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
            </RuntimeLink>
            <RuntimeLink href={secondaryHref || { slug: "contact" }} className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/45 bg-black/20 px-5 text-sm font-bold text-white/95 backdrop-blur-sm transition hover:border-white/70 hover:bg-black/30 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:px-6">
              <Phone className="h-4 w-4 shrink-0 text-[#5AD59B]" />
              <span className="md:hidden">Conseil expert</span>
              <span className="hidden md:inline">{secondaryLabel || "Parler à un spécialiste"}</span>
            </RuntimeLink>
          </div>

          <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-white/15 pt-3.5 md:mt-7 md:gap-x-5 md:gap-y-2.5 md:border-white/20 md:pt-5">
            {assuranceItems.map((item) => (
              <li key={item} className="flex items-center gap-2 text-xs font-semibold text-white/80 sm:text-sm">
                <Check className="h-4 w-4 text-[#00D477]" strokeWidth={3} /> {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
